import { paymentApi } from '~/shared/api'
import {
  APPLOVIN_CATEGORY_ID,
  Events,
  PaymentSystem,
  ScreenName,
  UpsellProduct,
} from '~/shared/constants'
import {
  getPurchaseFailedEventParams,
  getPurchaseStartedEventParams,
  getPurchaseSuccessEventParams,
} from '~/shared/helpers/purchaseEventParams'
import { logSuccessfulPayment } from '~/shared/helpers/stripePaymentLogger'
import { eventLogger } from '~/shared/services'
import { IAppState, TAppDispatchThunk } from '~/shared/store/types'

import { startFetching, stopFetching } from '../../common/actions'
import {
  getUserStatusAction,
  savePlanAdditionsAction,
} from '../../user/actions'
import {
  selectIsPersonalDataAllowed,
  selectUUID,
  selectUserPaymentSystem,
} from '../../user/selectors'
import {
  selectCurrency,
  selectInAppId,
  selectPaymentMethod,
  selectProductId,
  selectSubscription,
  selectSubscriptionTrialLookupKey,
  selectSubscriptionTrialPeriodDays,
  selectSubscriptionTrialPeriodPrice,
} from '../selectors'

const MODULE_NAME = 'UPSELL'

// actions types
export const MAKE_UPSELL = `${MODULE_NAME}/MAKE_UPSELL`

export const makeUpsellAction =
  (screenName: ScreenName): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const productId = selectProductId(state)
    const trialPriceId = selectSubscriptionTrialLookupKey(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const trialCurrentPrice = selectSubscriptionTrialPeriodPrice(state)
    const currency = selectCurrency(state)
    const selectedInAppId = selectInAppId(state)
    const selectedSubscription = selectSubscription(state)
    const paymentMethod = selectPaymentMethod(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isPersonalDataAllowed = selectIsPersonalDataAllowed(state)

    dispatch(startFetching(MAKE_UPSELL))

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      event: Events.PURCHASE_STARTED,
      screenName,
      priceDetails: { price: trialCurrentPrice, currency },
      productId: trialPriceId,
    })

    const dto = {
      uuid,
      productId: productId || UpsellProduct.BOTH_RECURRENT,
      planId: Number(selectedInAppId),
    }

    const response =
      paymentSystem === PaymentSystem.PRIMER
        ? await paymentApi.makePrimerUpsell(dto)
        : await paymentApi.makeUpsell(dto)

    if (response.success && response.data && selectedSubscription) {
      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        isUpsell: true,
        paymentSystem,
        price: trialCurrentPrice,
        trialPeriodDays,
        subscriptionId: response.data.subscription.subscription_id,
        paymentMethod,
        screenName,
        productId: response.data.subscription.product_id,
      })

      window?.axon?.('track', 'purchase', {
        value:
          trialCurrentPrice || trialCurrentPrice === 0
            ? trialCurrentPrice
            : commonPurchaseSuccessParams.price,
        currency: currency.toUpperCase(),
        user_id: isPersonalDataAllowed ? uuid : '',
        transaction_id: response.data.subscription.subscription_id,
        items: [
          {
            item_category_id: APPLOVIN_CATEGORY_ID,
            item_id: response.data.subscription.subscription_id,
            item_name: `${commonPurchaseSuccessParams.periodQuantity} ${commonPurchaseSuccessParams.periodName} plan`,
            price: trialCurrentPrice || commonPurchaseSuccessParams.price,
            quantity: 1,
          },
        ],
      })

      dispatch(savePlanAdditionsAction())
      dispatch(getUserStatusAction(uuid))
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    eventLogger.logPurchaseFailed({
      ...commonPurchaseFailedParams,
      paymentMethod,
      paymentSystem,
      error: { type: response.data, declineCode: response.data.decline_code },
      screenName,
      priceDetails: { price: trialCurrentPrice, currency },
      event: Events.PURCHASE_FAILED,
      productId: trialPriceId,
    })

    dispatch(getUserStatusAction(uuid))
    dispatch(stopFetching(MAKE_UPSELL))
  }

export const createInAppAction =
  (screenName: ScreenName): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const uuid = selectUUID(state)
    const trialPriceId = selectSubscriptionTrialLookupKey(state)
    const trialPeriodDays = selectSubscriptionTrialPeriodDays(state)
    const trialCurrentPrice = selectSubscriptionTrialPeriodPrice(state)
    const currency = selectCurrency(state)
    const selectedInAppId = selectInAppId(state)
    const selectedSubscription = selectSubscription(state)
    const paymentMethod = selectPaymentMethod(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isPersonalDataAllowed = selectIsPersonalDataAllowed(state)

    dispatch(startFetching(MAKE_UPSELL))

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem,
      event: Events.INAPP_PURCHASE_STARTED,
      screenName,
      priceDetails: { price: trialCurrentPrice, currency },
      productId: trialPriceId,
    })

    const response = await paymentApi.createInAppPurchase({
      uuid,
      planId: Number(selectedInAppId),
      productName: selectedSubscription?.product,
    })

    if (response.success && response.data && selectedSubscription) {
      const productId = `${response.data.inapp.id};${response.data.inapp.payment_id};0`

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        isInApp: true,
        eventName: Events.INAPP_PURCHASE_COMPLETED,
        price: trialCurrentPrice,
        trialPeriodDays,
        subscriptionId: response.data.inapp.payment_id,
        paymentMethod,
        paymentSystem,
        screenName,
        productId,
      })

      window?.axon?.('track', 'purchase', {
        value: trialCurrentPrice,
        currency: currency.toUpperCase(),
        user_id: isPersonalDataAllowed ? uuid : '',
        transaction_id:
          response.data.inapp.id || response.data.inapp.payment_id,
        items: [
          {
            item_category_id: APPLOVIN_CATEGORY_ID,
            item_id: response.data.inapp.id || response.data.inapp.product_id,
            item_name: `${productId}`,
            price: trialCurrentPrice,
            quantity: 1,
          },
        ],
      })

      dispatch(savePlanAdditionsAction())
      dispatch(stopFetching(MAKE_UPSELL))
      return
    }

    eventLogger.logPurchaseFailed({
      ...commonPurchaseFailedParams,
      paymentMethod,
      paymentSystem,
      error: { type: response.data, declineCode: response.data.decline_code },
      screenName,
      priceDetails: { price: trialCurrentPrice, currency },
      event: Events.INAPP_PURCHASE_FAILED,
      productId: trialPriceId,
    })

    dispatch(stopFetching(MAKE_UPSELL))
  }
